import { format, utcToZonedTime } from 'date-fns-tz';
import { v4 as uuid } from 'uuid';

import { findDateToMerge } from './findDateToMerge';
import { sortClassTimes } from './sortClassTimes';
import { uniqueDays } from './uniqueDays';
import {
    type ClassSessionGroupWithMembershipsRecord,
    type ClassSessionRecord,
    type SessionGroupConfig
} from '../../../../../models/types';
import { AttendanceType, type CalendarValues } from '../types';

interface AggregateCalendarDatesInterface {
    classTimes: string;
    timezone: string;
    classSessions?: ClassSessionRecord[];
    classSessionGroups?: ClassSessionGroupWithMembershipsRecord[];
}

export const aggregateCalendarDates = ({
    classTimes,
    timezone,
    classSessionGroups
}: AggregateCalendarDatesInterface): CalendarValues[] => {
    if (classTimes === '') {
        return [];
    }

    const startAndEndTimesToClassSessionGroupUuid: { [key: string]: string } = {};
    classSessionGroups?.forEach((classSessionGroup) => {
        if (classSessionGroup.schedule && classSessionGroup.schedule.class_times) {
            classSessionGroup.schedule.class_times.forEach((startAndEndTime) => {
                startAndEndTimesToClassSessionGroupUuid[startAndEndTime] = classSessionGroup.class_session_group_uuid;
            });
        }
    });
    const classTimesArray = sortClassTimes(classTimes);

    const calendarValues: CalendarValues[] = classTimesArray.map((startAndEndTime) => {
        const startTime = parseInt(startAndEndTime.split(':')[0], 10);
        const endTime = parseInt(startAndEndTime.split(':')[1], 10);

        const startDateUTC = new Date(startTime * 1000);
        const endDateUTC = new Date(endTime * 1000);

        // Convert UTC to the provided timezone
        const startDate = utcToZonedTime(startDateUTC, timezone);
        const endDate = utcToZonedTime(endDateUTC, timezone);

        return {
            days: [startDate.getDay()],
            timezone,
            dateRange: [startDate, startDate], // this gets updated as values are merged
            startTime: format(startDate, 'kk:mm', { timeZone: timezone }),
            endTime: format(endDate, 'kk:mm', { timeZone: timezone }),
            startAndEndTime,
            classSessionGroupUuid: startAndEndTimesToClassSessionGroupUuid[startAndEndTime]
        };
    });

    const mergedCalendarValues: CalendarValues[] = [];

    calendarValues.forEach((calendarValue, index, array) => {
        const { dateToMerge, dateToMergeInto, dateToMergeIntoIndex } = findDateToMerge({
            mergedCalendarValues,
            remainingCalendarValues: array.slice(index)
        });

        if (dateToMergeInto && typeof dateToMergeIntoIndex === 'number' && mergedCalendarValues.length !== 0) {
            const previousDays = mergedCalendarValues[dateToMergeIntoIndex].days;
            const nextDays = dateToMerge ? dateToMerge.days : [];

            const previousStartDate = mergedCalendarValues[dateToMergeIntoIndex].dateRange[0];
            const nextStartDate = dateToMerge ? dateToMerge.dateRange[1] : previousStartDate;

            // update old index; which isn't always the last index
            mergedCalendarValues[dateToMergeIntoIndex] = {
                ...mergedCalendarValues[dateToMergeIntoIndex],
                days: uniqueDays([...previousDays, ...nextDays]),
                dateRange: [previousStartDate, nextStartDate]
            };
        } else {
            mergedCalendarValues.push(calendarValue);
        }
    });

    const computeAttendanceValue = (config: SessionGroupConfig): AttendanceType => {
        if (!config?.trackAttendance) {
            return AttendanceType.NoAttendance;
        }
        return config?.attendanceLmsPush ? AttendanceType.IntegrateWithLms : AttendanceType.Native;
    };

    mergedCalendarValues.forEach((mergedCalendarValue) => {
        const matchingGroup = classSessionGroups?.find((classSessionGroup) => {
            const { schedule } = classSessionGroup;
            const { startAndEndTime } = mergedCalendarValue;
            return schedule?.class_times?.includes(startAndEndTime);
        });

        if (matchingGroup) {
            mergedCalendarValue.classSessionGroupUuid = matchingGroup.class_session_group_uuid;
            mergedCalendarValue.label = matchingGroup.config.label;
            mergedCalendarValue.selectedGroups = matchingGroup.config?.selectedGroups || ['all'];
            mergedCalendarValue.attendanceType = computeAttendanceValue(matchingGroup.config);
        } else {
            mergedCalendarValue.classSessionGroupUuid = uuid();
            mergedCalendarValue.label = '';
            mergedCalendarValue.selectedGroups = ['all'];
            mergedCalendarValue.attendanceType = AttendanceType.Native;
        }
    });

    return mergedCalendarValues;
};
